// src/pages/painel/RefeicaoModal.js
import React, { useEffect, useMemo, useState } from "react";
import "./refeicaoModal.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDrumstickBite,
  faUtensils,
  faBreadSlice,
  faCheese,
  faChevronDown,
  faChevronUp,
  faClock,
  faFireFlameCurved,
  faListUl,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartOutline } from "@fortawesome/free-regular-svg-icons";

const API_BASE = process.env.REACT_APP_API_BASE;

const RefeicaoModal = ({ refeicao, onClose, onFavoritoSalvo }) => {
  const [mostrarItens, setMostrarItens] = useState(false);

  // 🆕 estados do toggle de favorito
  const [verificandoStatus, setVerificandoStatus] = useState(true);
  const [jaFavoritado, setJaFavoritado] = useState(false);
  const [favoritoId, setFavoritoId] = useState(null);
  const [processandoFavorito, setProcessandoFavorito] = useState(false);
  const [erroFavorito, setErroFavorito] = useState("");

  const detalhesJson = refeicao?.detalhes_itens_json ?? null;

  const itensDetalhados = useMemo(() => {
    if (!detalhesJson) return [];
    try {
      const parsed = JSON.parse(detalhesJson);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      return [];
    } catch (e) {
      console.warn("Erro ao parsear detalhes_itens_json:", e);
      return [];
    }
  }, [detalhesJson]);

  // ==========================================================
  // 🆕 Checar status do favorito ao abrir o modal
  // ==========================================================
  useEffect(() => {
    if (!refeicao?.id) {
      setVerificandoStatus(false);
      return;
    }

    const verificar = async () => {
      setVerificandoStatus(true);
      setErroFavorito("");

      try {
        const res = await fetch(
          `${API_BASE}/favorito-status.php?registro_id=${refeicao.id}`,
          { credentials: "include" }
        );
        const data = await res.json();

        if (data.status === "sucesso") {
          setJaFavoritado(!!data.ja_favoritado);
          setFavoritoId(data.favorito_id ?? null);
        }
      } catch {
        // se falhar, assume não-favoritado (comportamento seguro: só oferece salvar)
        setJaFavoritado(false);
        setFavoritoId(null);
      }

      setVerificandoStatus(false);
    };

    verificar();
  }, [refeicao?.id]);

  if (!refeicao) return null;

  const {
    refeicao_tipo_id,
    nome_refeicao,
    calorias_total,
    proteinas_total,
    carboidratos_total,
    gorduras_total,
    descricao_original,
    data_hora_refeicao,
  } = refeicao;

  const obterNomeRefeicao = (id) => {
    if (!id && id !== 0) return "Refeição";
    switch (Number(id)) {
      case 1:
        return "Café da manhã";
      case 2:
        return "Almoço";
      case 3:
        return "Jantar";
      case 4:
        return "Lanche";
      default:
        return nome_refeicao || "Refeição";
    }
  };

  const obterIconeRefeicao = (id) => {
    switch (Number(id)) {
      case 1:
        return <FontAwesomeIcon icon={faDrumstickBite} />;
      case 2:
        return <FontAwesomeIcon icon={faUtensils} />;
      case 3:
        return <FontAwesomeIcon icon={faBreadSlice} />;
      case 4:
        return <FontAwesomeIcon icon={faCheese} />;
      default:
        return <FontAwesomeIcon icon={faUtensils} />;
    }
  };

  const formatNumero = (valor, casas = 1) => {
    const n = Number(valor || 0);
    if (!Number.isFinite(n)) return "0";
    return n.toFixed(casas).replace(".", ",");
  };

  const formatKcal = (valor) => {
    const n = Number(valor || 0);
    if (!Number.isFinite(n)) return "0";
    return n.toFixed(0);
  };

  const formatarDataHora = (str) => {
    if (!str) return "";
    const isoLike = str.replace(" ", "T");
    const d = new Date(isoLike);
    if (Number.isNaN(d.getTime())) return "";
    const data = d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const hora = d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${data} às ${hora}`;
  };

  const tituloRefeicao = obterNomeRefeicao(refeicao_tipo_id);
  const dataHoraFormatada = formatarDataHora(data_hora_refeicao);

  const descricaoLimpa =
    typeof descricao_original === "string" && descricao_original.trim().length > 0
      ? descricao_original.trim()
      : null;

  // ==========================================================
  // 🆕 SALVAR COMO FAVORITO
  // ==========================================================
  const salvarComoFavorito = async () => {
    if (processandoFavorito || jaFavoritado) return;

    setProcessandoFavorito(true);
    setErroFavorito("");

    try {
      const res = await fetch(`${API_BASE}/salvar-favorito-pessoal.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          registro_id_origem: refeicao.id, // 🆕
          refeicao_tipo_id,
          descricao_original,
          calorias_total,
          proteinas_total,
          carboidratos_total,
          gorduras_total,
          detalhes_itens_json: detalhesJson,
        }),
      });

      const data = await res.json();

      if (data.status === "sucesso") {
        setJaFavoritado(true);
        setFavoritoId(data.favorito?.id ?? null);
        onFavoritoSalvo?.(data.novas_conquistas || []);
      } else if (data.motivo === "favorito_duplicado") {
        // Já era favorito (dado dessincronizado) — corrige o estado local
        setJaFavoritado(true);
        setErroFavorito("");
      } else {
        setErroFavorito(data.mensagem || "Erro ao salvar favorito.");
      }
    } catch {
      setErroFavorito("Erro ao comunicar com o servidor.");
    }

    setProcessandoFavorito(false);
  };

  // ==========================================================
  // 🆕 REMOVER DOS FAVORITOS
  // ==========================================================
  const removerDosFavoritos = async () => {
    if (processandoFavorito || !jaFavoritado || !favoritoId) return;

    setProcessandoFavorito(true);
    setErroFavorito("");

    try {
      const res = await fetch(`${API_BASE}/remover-favorito.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          id: favoritoId,
          fonte: "pessoal",
        }),
      });

      const data = await res.json();

      if (data.status === "sucesso") {
        setJaFavoritado(false);
        setFavoritoId(null);
      } else {
        setErroFavorito(data.mensagem || "Erro ao remover favorito.");
      }
    } catch {
      setErroFavorito("Erro ao comunicar com o servidor.");
    }

    setProcessandoFavorito(false);
  };

  const handleCliqueFavorito = () => {
    if (jaFavoritado) {
      removerDosFavoritos();
    } else {
      salvarComoFavorito();
    }
  };

  return (
    <div className="modal-fundo refeicao-modal-fundo" onClick={onClose}>
      <div
        className="modal-referencia refeicao-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="modal-fechar refeicao-modal-fechar"
          onClick={onClose}
          aria-label="Fechar detalhes da refeição"
        >
          ×
        </button>

        <header className="refeicao-modal-header">
          <div className="refeicao-modal-header-esquerda">
            <div className="refeicao-modal-icone">
              {obterIconeRefeicao(refeicao_tipo_id)}
            </div>
            <div className="refeicao-modal-titulos">
              <h2>{tituloRefeicao}</h2>
              {dataHoraFormatada && (
                <div className="refeicao-modal-data">
                  <FontAwesomeIcon icon={faClock} className="refeicao-modal-data-icone" />
                  <span>{dataHoraFormatada}</span>
                </div>
              )}
            </div>
          </div>

          <div className="refeicao-modal-kcal-highlight">
            <FontAwesomeIcon icon={faFireFlameCurved} className="refeicao-modal-kcal-icone" />
            <div className="refeicao-modal-kcal-texto">
              <span className="refeicao-modal-kcal-valor">
                {formatKcal(calorias_total)}
              </span>
              <span className="refeicao-modal-kcal-label">kcal</span>
            </div>
          </div>
        </header>

        {descricaoLimpa && (
          <section className="refeicao-modal-descricao-original">
            <span className="refeicao-modal-descricao-label">Você registrou:</span>
            <p className="refeicao-modal-descricao-texto">“{descricaoLimpa}”</p>
          </section>
        )}

        {/* 🆕 BOTÃO TOGGLE DE FAVORITO */}
        <section className="refeicao-modal-favorito-bloco">
          {!verificandoStatus && (
            <button
              type="button"
              className={`refeicao-modal-btn-favorito ${jaFavoritado ? "salvo" : ""}`}
              onClick={handleCliqueFavorito}
              disabled={processandoFavorito}
            >
              <FontAwesomeIcon icon={jaFavoritado ? faHeart : faHeartOutline} />
              {processandoFavorito
                ? "Aguarde..."
                : jaFavoritado
                ? "Remover dos favoritos"
                : "Salvar como favorito"}
            </button>
          )}

          {erroFavorito && (
            <p className="refeicao-modal-favorito-erro">{erroFavorito}</p>
          )}
        </section>

        <section className="refeicao-modal-macros-bloco">
          <h3 className="refeicao-modal-subtitulo">Resumo nutricional</h3>

          <div className="refeicao-modal-macros-grid">
            <div className="refeicao-modal-macro-card">
              <span className="refeicao-modal-macro-label">Proteínas</span>
              <strong className="refeicao-modal-macro-valor">
                {formatNumero(proteinas_total, 1)} g
              </strong>
            </div>
            <div className="refeicao-modal-macro-card">
              <span className="refeicao-modal-macro-label">Carboidratos</span>
              <strong className="refeicao-modal-macro-valor">
                {formatNumero(carboidratos_total, 1)} g
              </strong>
            </div>
            <div className="refeicao-modal-macro-card">
              <span className="refeicao-modal-macro-label">Gorduras</span>
              <strong className="refeicao-modal-macro-valor">
                {formatNumero(gorduras_total, 1)} g
              </strong>
            </div>
          </div>
        </section>

        <section className="refeicao-modal-detalhes-section">
          <button
            type="button"
            className="refeicao-modal-detalhes-toggle"
            onClick={() => setMostrarItens((prev) => !prev)}
          >
            <div className="refeicao-modal-detalhes-left">
              <FontAwesomeIcon icon={faListUl} className="refeicao-modal-detalhes-icone" />
              <span>
                {mostrarItens
                  ? "Ocultar detalhes dos itens"
                  : "Ver detalhes dos itens da refeição"}
              </span>
            </div>
            <FontAwesomeIcon
              icon={mostrarItens ? faChevronUp : faChevronDown}
              className="refeicao-modal-detalhes-seta"
            />
          </button>

          {mostrarItens && (
            <div className="refeicao-modal-itens-wrapper">
              {itensDetalhados.length === 0 && (
                <p className="refeicao-modal-itens-vazio">
                  Não há detalhes por item disponíveis para esta refeição.
                </p>
              )}

              {itensDetalhados.length > 0 && (
                <ul className="refeicao-modal-itens-lista">
                  {itensDetalhados.map((item, index) => {
                    const desc =
                      typeof item.descricao === "string" && item.descricao.trim().length > 0
                        ? item.descricao.trim()
                        : "Item";

                    const quantidade =
                      item.quantidade_gramas != null
                        ? `${formatNumero(item.quantidade_gramas, 0)} g`
                        : item.quantidade_ml != null
                        ? `${formatNumero(item.quantidade_ml, 0)} ml`
                        : item.quantidade != null
                        ? String(item.quantidade)
                        : null;

                    const infoExtraParts = [];

                    if (item.calorias_item != null) {
                      infoExtraParts.push(
                        `${formatNumero(item.calorias_item, 0)} kcal`
                      );
                    }

                    if (
                      item.proteinas_item != null ||
                      item.carboidratos_item != null ||
                      item.gorduras_item != null
                    ) {
                      const partesMacros = [];
                      if (item.proteinas_item != null) {
                        partesMacros.push(
                          `${formatNumero(item.proteinas_item, 1)} g P`
                        );
                      }
                      if (item.carboidratos_item != null) {
                        partesMacros.push(
                          `${formatNumero(item.carboidratos_item, 1)} g C`
                        );
                      }
                      if (item.gorduras_item != null) {
                        partesMacros.push(
                          `${formatNumero(item.gorduras_item, 1)} g G`
                        );
                      }
                      if (partesMacros.length > 0) {
                        infoExtraParts.push(partesMacros.join(" · "));
                      }
                    }

                    const infoExtra =
                      infoExtraParts.length > 0
                        ? infoExtraParts.join(" | ")
                        : null;

                    return (
                      <li key={index} className="refeicao-modal-item">
                        <div className="refeicao-modal-item-linha1">
                          <span className="refeicao-modal-item-descricao">
                            {desc}
                          </span>
                          {quantidade && (
                            <span className="refeicao-modal-item-quantidade">
                              {quantidade}
                            </span>
                          )}
                        </div>
                        {infoExtra && (
                          <div className="refeicao-modal-item-infoextra">
                            {infoExtra}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default RefeicaoModal;